import { baseApi } from '@/shared/api/baseApi'
import { customErrorHandler } from '@/shared/api/customErrorHandler'
import { TAGS } from '@/shared/api/tags'
import type {
	GetUsersModel,
	GetUsersVariables,
	MostSavedMonthlyMembersModel,
	MostSavedMonthlyMembersVariables,
	StatusName,
	UpdateUserRoleVariables,
	UserDTO
} from '../model/types'

// Helper to create query string
const createQueryString = (
	params: Record<string, string | number | string[] | undefined>
): string => {
	const searchParams = new URLSearchParams()
	Object.entries(params).forEach(([key, value]) => {
		if (value !== undefined && value !== '') {
			if (Array.isArray(value)) {
				// For arrays, add multiple values with the same key
				value.forEach((v) => {
					searchParams.append(key, String(v))
				})
			} else {
				searchParams.append(key, String(value))
			}
		}
	})
	const queryString = searchParams.toString()
	return queryString ? `?${queryString}` : ''
}

export const userAdminApi = baseApi.injectEndpoints({
	endpoints: (build) => ({
		getUsers: build.query<GetUsersModel, GetUsersVariables>({
			query: (params) => `/users${createQueryString(params)}`,
			transformErrorResponse: customErrorHandler,
			providesTags: [TAGS.USERS]
		}),
		updateUser: build.mutation<void, { userId: number; status?: StatusName }>({
			query: ({ userId, ...args }) => ({
				method: 'PUT',
				url: `/users/${userId}`,
				body: args
			}),
			transformErrorResponse: customErrorHandler,
			invalidatesTags: [TAGS.USERS]
		}),
		deleteUser: build.mutation<void, { userId: number }>({
			query: ({ userId }) => ({
				method: 'DELETE',
				url: `/users/${userId}`
			}),
			transformErrorResponse: customErrorHandler,
			invalidatesTags: [TAGS.USERS]
		}),
		updateUserRole: build.mutation<UserDTO, UpdateUserRoleVariables>({
			query: ({ userId, roleCode }) => ({
				url: `/users/${userId}/role`,
				method: 'PUT',
				body: { role: roleCode }
			}),
			transformErrorResponse: customErrorHandler,
			invalidatesTags: [TAGS.USERS, TAGS.ME]
		}),
		getUserStatistics: build.query<
			MostSavedMonthlyMembersModel,
			MostSavedMonthlyMembersVariables
		>({
			query: (params) => `/users/statistics${createQueryString(params)}`,
			transformErrorResponse: customErrorHandler,
			providesTags: [TAGS.USERS]
		}),
		getAdminUserInfo: build.query<UserDTO, { userId: string | number }>({
			query: ({ userId }) => `/users/admin/${userId}/info`,
			transformErrorResponse: customErrorHandler,
			providesTags: [TAGS.USERS]
		})
	})
})

export const {
	useGetUsersQuery,
	useUpdateUserMutation,
	useDeleteUserMutation,
	useUpdateUserRoleMutation,
	useGetUserStatisticsQuery,
	useGetAdminUserInfoQuery
} = userAdminApi
